import { ButtonBase, ListSubheader, MenuItem } from '@mui/material'
import { compact } from 'lodash-es'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import IcBaselineFlag from '~icons/ic/baseline-flag'
import IcBaselineMoreVert from '~icons/ic/baseline-more-vert'
import {
  dialogModerationArchiveEdit,
  dialogModerationEdit,
} from 'src/components/Admin/Moderation/ModerationEditDialog'
import DropdownMenu from 'src/components/DropdownMenu'
import HelpIcon from 'src/components/HelpIcon'
import { UserCancelled } from 'src/components/Modal/Confirm'
import { dialogInspection } from 'src/components/Modal/InspectDialog'
import { dialogReport } from 'src/components/Report/ReportDialog'
import { UIDialog } from 'src/components/ui/Modal'
import { useMyInfo } from 'src/services/apollo/auth'
import type { Manga } from 'src/services/apollo/generated/graphql'
import { RefType } from 'src/services/apollo/generated/graphql'
import { executeDeleteManga } from 'src/services/apollo/manga'
import { track } from 'src/utils/tracker'

export const confirmDeleteManga = async () => {
  const toProceed = await UIDialog.confirm({
    heading: 'Delete manga?',
    content: 'Are you sure you want to delete this manga?',
    confirmProps: {
      color: 'danger',
      children: 'Delete',
    },
  })
  if (!toProceed) throw UserCancelled
}
const MangaExtraActions: FC<{
  manga?: Manga
}> = ({ manga }) => {
  const { t } = useTranslation(['manga', 'artwork'])

  const { data: userInfo } = useMyInfo()

  if (!manga || !userInfo) return null

  const selfManga = manga.authorId === userInfo.id
  const isAdmin = userInfo.isAdmin

  const report = async () => {
    if (!manga.artworkId) return toast.error('This manga cannot be reported')
    dialogReport(RefType.Artwork, manga.artworkId)
  }

  const deleteManga = async () => {
    try {
      track('delete_manga_click')
      await confirmDeleteManga()
      await executeDeleteManga({
        id: manga.id,
      })
      toast.success('Delete successfully.')
    } catch (err: any) {
      if (err === UserCancelled) return
      toast.error(`Failed to delete. ${err?.message}`)
    }
  }

  const markModerationNsfw = async () => {
    if (!manga.artworkId) return toast.error('This manga cannot be moderated')
    dialogModerationEdit({
      refIds: [manga.artworkId],
      refType: RefType.Artwork,
      shouldFetch: true,
    })
  }

  const markMangaArchiveStatus = async () => {
    if (!manga.artworkId)
      return toast.error('This manga cannot be adjusted archive status')
    dialogModerationArchiveEdit({
      refId: manga.artworkId,
      refType: RefType.Artwork,
      shouldFetch: true,
    })
  }

  const inspect = () => {
    dialogInspection({
      manga,
    })
  }

  const menuItems = compact([
    userInfo && !selfManga && (
      <MenuItem onClick={report} key="report">
        <IcBaselineFlag className="size-6 mr-2 opacity-80" />
        {t('artwork:action.report')}
      </MenuItem>
    ),

    !selfManga && isAdmin && (
      <ListSubheader
        className="flex items-center gap-4 font-bold"
        key="subheader-admin"
      >
        <span>Admin (override) Actions</span>
        <HelpIcon>
          <div className="p-4 text-sm w-80">
            This artwork is not yours, but you are admin so you can access these
            actions.
          </div>
        </HelpIcon>
      </ListSubheader>
    ),
    ...(selfManga || isAdmin
      ? [
          <MenuItem key="delete" onClick={deleteManga}>
            <span className="text-red-600">{t('manga:delete')}</span>
          </MenuItem>,
        ]
      : []),

    isAdmin && (
      <ListSubheader
        className="flex items-center gap-4 font-bold bg-orange-50 hover:bg-orange-100 dark:bg-orange-700 dark:text-orange-50"
        key="subheader-admin-moderation"
      >
        <span>Admin Actions</span>
        <HelpIcon>
          <div className="p-4 text-sm w-80">
            You are admin so you can access these actions.
          </div>
        </HelpIcon>
      </ListSubheader>
    ),

    ...(isAdmin
      ? [
          <MenuItem
            onClick={() => markModerationNsfw()}
            key="mark-moderation-sfw"
            className=""
          >
            <span className="text-orange-600">Adjust Manga Moderation</span>
          </MenuItem>,

          <MenuItem
            key="archive"
            onClick={() => {
              markMangaArchiveStatus()
            }}
            className="text-orange-600"
          >
            Adjust Manga Archive status
          </MenuItem>,

          <MenuItem
            onClick={inspect}
            key="inspect"
            className="dark:text-orange-50"
          >
            Inspect
          </MenuItem>,
        ]
      : []),
  ])

  if (menuItems.length === 0) return null

  return (
    <DropdownMenu
      anchor={
        <ButtonBase>
          <IcBaselineMoreVert className="size-6" />
        </ButtonBase>
      }
    >
      {menuItems}
    </DropdownMenu>
  )
}

export default MangaExtraActions
